/**
 * Media dialogs, ported from the Claude Design canvas ("Setbox Home",
 * 2026-09-16).
 *
 * Several screens open a clip full screen when its card is clicked. The canvas
 * keeps that in React state and renders its own overlay; here it is a native
 * <dialog>, which brings the focus trap, the Escape key and the backdrop for
 * free instead of reimplementing them.
 *
 * Openers carry data-setbox-dialog-open="<id>", the dialog has that id.
 *
 * Sound: the clip opens muted and keeps the browser's own controls, which is
 * what the canvas does. Unmuting on open was wrong twice over — it is not the
 * design, and audio starting by itself is not something a visitor asked for.
 */
(() => {
  const OPEN = '[data-setbox-dialog-open]';
  const CLOSE = '[data-setbox-dialog-close]';

  const media = (dialog) => dialog.querySelectorAll('video, audio');

  /**
   * YouTube dialogs hold only an id until they are opened. Building the iframe
   * on demand keeps a third-party frame off every page view — the canvas
   * mounts it the same way, and here it also means nothing from youtube.com is
   * requested unless a visitor asks for the clip.
   */
  const mountFrame = (dialog) => {
    const slot = dialog.querySelector('[data-youtube-slot]');
    const id = dialog.dataset.youtubeId;
    if (!slot || !id || slot.firstElementChild) return;

    const frame = document.createElement('iframe');
    frame.src =
      'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
      '?autoplay=1&mute=1&rel=0&playsinline=1';
    frame.title = dialog.getAttribute('aria-label') || '';
    frame.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.allowFullscreen = true;
    frame.loading = 'lazy';
    slot.appendChild(frame);
  };

  const unmountFrame = (dialog) => {
    const slot = dialog.querySelector('[data-youtube-slot]');
    if (slot) slot.replaceChildren();
  };

  const open = (dialog) => {
    if (typeof dialog.showModal !== 'function') return;
    dialog.showModal();
    mountFrame(dialog);
    media(dialog).forEach((el) => {
      // Muted is also what makes autoplay allowed at all; the controls are
      // right there for anyone who wants the audio.
      el.muted = true;
      const play = el.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    });
  };

  const close = (dialog) => {
    media(dialog).forEach((el) => {
      el.pause();
      el.currentTime = 0;
      el.muted = true;
    });
    // Removing the frame is what actually stops YouTube playing; pausing it
    // would need their JS API loaded, which is the thing being avoided.
    unmountFrame(dialog);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest(OPEN);
    if (opener) {
      const dialog = document.getElementById(opener.dataset.setboxDialogOpen);
      if (dialog) {
        event.preventDefault();
        open(dialog);
      }
      return;
    }

    const closer = event.target.closest(CLOSE);
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    // Clicking the backdrop: the event target is the dialog itself, because
    // the content sits in a child element that would otherwise be the target.
    const dialog = event.target.closest('dialog.setbox-dialog');
    if (dialog && event.target === dialog) dialog.close();
  });

  // Capture phase, and this is not a detail: a <dialog>'s `close` event does
  // not bubble, so a listener on document in the bubble phase never runs —
  // which is why playback used to carry on after the dialog was dismissed.
  // Capturing listeners still see the event on its way down to the target.
  document.addEventListener(
    'close',
    (event) => {
      if (event.target instanceof HTMLDialogElement) close(event.target);
    },
    true
  );
})();
